import { useFormContext } from "react-hook-form";
import { classNames, getSafeArray, navigateTo } from "../../helper";
import { PiCheckBold } from "react-icons/pi";
import { useEffect, useMemo } from "react";
import { PlanCard } from "../../components/PlanCard";
import { useTranslation } from "react-i18next";
import { RootState } from "../../store/root-reducer";
import { connect, useDispatch } from "react-redux";
import { removeClaimItem } from "../../store/claim/actions";

function SelectItems({
  warranty_items,
  claim_items,
  warranty_id,
  claimId,
  plan,
}) {
  const { t } = useTranslation();
  const { setValue, getValues } = useFormContext();
  const dispatch = useDispatch();
  const mapped_warranty_items = useMemo(() => {
    const claimsByWarranty = claim_items.reduce((acc, ci) => {
      if (!acc[ci.warrntyItemId]) acc[ci.warrntyItemId] = [];
      acc[ci.warrntyItemId].push(ci);
      return acc;
    }, {});

    const usedCountByWarranty = {};

    return warranty_items.map((item) => {
      const claimsForWarranty = claimsByWarranty[item.id] || [];
      const usedSoFar = usedCountByWarranty[item.id] || 0;

      const claimItem = claimsForWarranty[usedSoFar] || null;
      const isSelected = Boolean(claimItem);

      if (isSelected) {
        usedCountByWarranty[item.id] = usedSoFar + 1;
      }

      return {
        type: item.type,
        itemDescription: item.itemDescription,
        sku: item.sku,
        location: item.location,
        returned: item.returned,
        fullReplacement: item.full_replacement,
        warrntyItemId: item?.id,
        trackKey: item?.trackKey,
        isSelected,
        isSavedInDB: Boolean(claimItem && claimItem?.id),
        claimItemId: claimItem?.id ?? null,
      };
    });
  }, [claim_items, warranty_items]);

  useEffect(() => {
    const currentSelected = getValues("selectedItems");
    if (currentSelected && currentSelected.length > 0) return;
    const selected = mapped_warranty_items
      .filter(({ isSelected }) => isSelected)
      .map(({ trackKey }) => trackKey);
    setValue("selectedItems", selected, { shouldValidate: true });
  }, [setValue, getValues, mapped_warranty_items]);

  const handleUnChecked = (item) => {
    if (item?.isSavedInDB) {
      dispatch(removeClaimItem({ id: item?.claimItemId }));
    }
  };

  return (
    <div className="space-y-2">
      <PlanCard plan={plan} />
      <span className="text-[#737B84] text-sm">
        {mapped_warranty_items.length} {t("items founds")}
      </span>
      <div className="space-y-3">
        {mapped_warranty_items.map((item, index) => (
          <ClaimItemBox
            key={item.trackKey}
            item={item}
            index={index}
            handleUnChecked={handleUnChecked}
          />
        ))}
        <button
          className="border-dashed border-2 border-brand-color my-4 flex disabled:cursor-not-allowed flex-row justify-center items-center gap-2 text-brand-color h-12 font-semibold rounded-xl w-full px-3 py-4"
          onClick={() =>
            navigateTo(
              `/add-warrnty-item?step=1&warranty_id=${warranty_id}&claimId=${claimId}`
            )
          }
          type="button"
        >
          {t("+ Add Missing Item")}
        </button>
      </div>
    </div>
  );
}

const ClaimItemBox = ({ item, index, handleUnChecked }) => {
  const { register, watch, unregister, setValue } = useFormContext();
  const selectedItems = watch("selectedItems");
  const selectedItemsField = register("selectedItems", {
    validate: (value) => value?.length > 0,
  });

  const checked = useMemo(() => {
    return selectedItems.some(
      (selectedId) => String(selectedId) === String(item.trackKey)
    );
  }, [selectedItems, item.trackKey]);

  const disabled = item.returned || item.full_replacement;
  const { t } = useTranslation();

  return (
    <label
      className={classNames(
        checked ? "border-brand-color" : "border-[#DFDFDF]",
        "border flex transition-all rounded-xl overflow-hidden cursor-pointer py-2 select-none items-center relative",
        disabled && "bg-gray-50 border-gray-200 !cursor-not-allowed"
      )}
    >
      <input
        type="checkbox"
        className="hidden"
        value={item.trackKey}
        disabled={disabled}
        {...selectedItemsField}
        onChange={(e) => {
          const currentArray = getSafeArray(selectedItems);
          const clickedValue = item.trackKey;
          let newValue = [];
          if (e.target.checked) {
            newValue = [...currentArray, clickedValue];
          } else {
            newValue = currentArray.filter(
              (val) => String(val) !== String(clickedValue)
            );
            handleUnChecked(item, index);
            unregister(`claimItems.[${item.trackKey}]`);
            unregister(`claimItemDamage.[${item.claimItemId}]`);
          }
          setValue("selectedItems", newValue, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }}
      />
      {disabled && item.returned && (
        <p className="absolute top-0 right-0 text-xs px-2 py-1 bg-red-100 text-red-700 rounded-bl-md">
          {t("Returned")}
        </p>
      )}
      {disabled && item.fullReplacement && (
        <p className="absolute top-0 right-0 text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-bl-md">
          {t("Full Replaced")}
        </p>
      )}
      <div className="w-9">
        <div
          className={classNames(
            checked ? "border-[#2D6CCA]" : "border-[#DFDFDF]",
            "h-4 m-2 w-4 shrink-0 cursor-pointer bg-white rounded border flex items-center justify-center transition-all",
            disabled && "bg-gray-100/80 opacity-50 cursor-not-allowed"
          )}
        >
          {checked && <PiCheckBold className="size-3 text-[#2D6CCA]" />}
        </div>
      </div>
      <div className={`w-full ${disabled && "opacity-30"}`}>
        <p className="text-black text-sm font-medium block">
          {item?.type?.name || item.itemDescription || "-"}
        </p>
        {item?.itemDescription && (
          <p className="text-[#949494] text-sm font-medium block">
            {item?.itemDescription}
          </p>
        )}
        {(item?.sku || item?.location) && (
          <div className="flex justify-between w-full pr-3">
            <div className="text-[#2D6CCA] text-sm font-semibold">
              {item?.sku}
            </div>
            <div className="text-sm text-black font-medium">
              {item?.location}
            </div>
          </div>
        )}
      </div>
    </label>
  );
};

const mapStateToProps = (state: RootState) => ({
  warranty_items: state?.claim?.warranty_items || [],
  claim_items: state?.claim?.claim_items || [],
  warranty_id: state?.claim?.plan?.["id"] || "",
  claimId: state?.claim?.claimId || "",
  plan: state.claim.plan || {},
});

export default connect(mapStateToProps)(SelectItems);
